import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, ClipboardCheck, Banknote, RotateCcw, Store } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ErrorBlock, Price, Spinner } from "../components/ui.tsx";
import { cn } from "@/lib/utils";
import { api } from "../api/client.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  后台首页 · 概览
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **以前打开后台，第一眼是「商品审核」的一张空表。**
 *    运营开后台想知道的是"今天有什么等着我处理"，而不是某一个页签的列表 ——
 *    没有这一页，他得挨个点过三个审核台才知道有没有事，
 *    而漏审一笔提现就是有人拿不到钱。
 *
 * 🔴 **系统健康也放在这儿。** 备份不跑、错误没人看，这两件事
 *    **不会报错，只会什么都不发生** —— 真出事时才发现"原来一直没备份"就晚了。
 *
 * ⚠️ 每一块都是**能点进去的**：概览的职责不是展示数字，是把人送到该去的那一页。
 */

interface Overview {
  todo: {
    products: number;
    withdrawals: number;
    refunds: number;
    orgs: number;
    unfulfillable: number;
  };
  stats: { ordersToday: number; orders7: number; gmv7: number; newUsers7: number };
  health: { openErrors: number; lastBackupAt: string | null; backupStale: boolean };
}

/** 待办卡。⚠️ 有事的时候要**跳出来**，没事时安静地待着 —— 都一个样等于没提示。 */
function TodoCard({
  icon: Icon,
  label,
  count,
  to,
  search,
  unit = "件",
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  to: string;
  search: Record<string, unknown>;
  unit?: string;
}) {
  const busy = count > 0;
  return (
    <Link
      to={to}
      search={search as never}
      className={cn(
        "group flex items-center gap-4 rounded-lg border px-5 py-4 transition-colors",
        busy
          ? "border-brand-200 bg-brand-50/60 hover:bg-brand-50"
          : "border-border bg-background hover:border-brand-200",
      )}
    >
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-lg",
          busy ? "bg-brand-600 text-white" : "bg-secondary text-muted-foreground",
        )}
      >
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] text-muted-foreground">{label}</span>
        <span className="mt-0.5 flex items-baseline gap-1">
          <span
            className={cn(
              "text-[26px] leading-none font-medium tabular-nums",
              busy ? "text-brand-700" : "text-muted-foreground/60",
            )}
          >
            {count}
          </span>
          <span className="text-[12px] text-muted-foreground">{unit}</span>
        </span>
      </span>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function StatCell({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="px-5 py-4">
      <p className="text-[13px] text-muted-foreground">{label}</p>
      <p className="mt-1 text-[24px] leading-none font-medium tabular-nums">{children}</p>
      {hint && <p className="mt-1.5 text-[12px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function OverviewTab() {
  const q = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api.get<Overview>("/admin/overview"),
    /** ⚠️ 后台常年开着，30 秒刷一次 —— 待办数隔一天不变会让人以为没事。 */
    refetchInterval: 30_000,
  });

  if (q.isPending) return <Spinner compact />;
  if (q.error) return <ErrorBlock error={q.error} />;

  const { todo, stats, health } = q.data;
  const totalTodo = todo.products + todo.withdrawals + todo.refunds + todo.orgs;

  return (
    <div className="space-y-6">
      {/* ══════════ 系统健康：有问题才出现 ══════════ */}
      {/*
        🔴 备份没成功过是**最容易被忽略的故障**：它不报错，只是什么都不发生。
           所以有问题时顶到最上面，没问题时整块不出现（别让人天天看一条绿色的"正常"）。
      */}
      {(health.backupStale || health.openErrors > 0) && (
        <Link
          to="/admin"
          search={{ tab: "health" } as never}
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 transition-colors hover:bg-amber-100/70"
        >
          <AlertTriangle className="mt-0.5 size-[18px] shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-amber-900">
            {health.backupStale && (
              <span className="block">
                <strong>数据库备份已过期</strong>
                {health.lastBackupAt
                  ? `（上次成功 ${new Date(health.lastBackupAt).toLocaleString("zh-CN")}）`
                  : "（从来没成功过）"}
                —— 它不跑的时候不会报错，只会什么都不发生。
              </span>
            )}
            {health.openErrors > 0 && (
              <span className="block">
                有 <strong>{health.openErrors}</strong> 类未处理的错误。
              </span>
            )}
          </span>
          <ArrowRight className="mt-0.5 size-4 shrink-0 text-amber-700" />
        </Link>
      )}

      {/* ══════════ 待办 ══════════ */}
      <section>
        <div className="mb-3 flex items-baseline gap-3">
          <h2 className="text-[15px] font-medium">等着处理</h2>
          <span className="text-[13px] text-muted-foreground">
            {totalTodo > 0 ? `共 ${totalTodo} 件` : "都处理完了"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          <TodoCard
            icon={ClipboardCheck}
            label="待审商品"
            count={todo.products}
            to="/admin"
            search={{ tab: "review" }}
          />
          <TodoCard
            icon={Banknote}
            label="待审提现"
            count={todo.withdrawals}
            to="/admin"
            search={{ tab: "withdrawals" }}
            unit="笔"
          />
          <TodoCard
            icon={RotateCcw}
            label="待审退款"
            count={todo.refunds}
            to="/admin"
            search={{ tab: "refunds" }}
            unit="笔"
          />
          {/* ⚠️ 待审商家不批他就永远开不了张，而他自己不知道卡在哪。 */}
          <TodoCard
            icon={Store}
            label="待审商家"
            count={todo.orgs}
            to="/admin"
            search={{ tab: "orgs" }}
            unit="家"
          />
        </div>

        {/*
          🔴 「钱收了但发不出去」的单：钱在平台账上、用户手里没货，只能人工退款。
             不摆出来等于把问题埋起来 —— 用户会来投诉，而客服查不到任何异常。
        */}
        {todo.unfulfillable > 0 && (
          <p className="mt-3 rounded-lg border border-destructive/20 bg-destructive/5 px-5 py-3 text-[13px] text-destructive">
            🔴 有 <strong>{todo.unfulfillable}</strong> 单「钱收了但发不出去」——
            钱在平台账上、用户手里没货，只能人工退款。
          </p>
        )}
      </section>

      {/* ══════════ 近 7 天 ══════════ */}
      <section>
        <h2 className="mb-3 text-[15px] font-medium">近 7 天</h2>
        <div className="grid grid-cols-2 divide-x divide-y divide-border rounded-lg border border-border bg-background md:grid-cols-4 md:divide-y-0">
          <StatCell label="今日新单">{stats.ordersToday}</StatCell>
          <StatCell label="7 天新单">{stats.orders7}</StatCell>
          {/* ⚠️ 只算已付款的：待付款的单随时会取消，算进成交额是虚的。 */}
          <StatCell label="7 天成交额" hint="只算已付款的">
            <Price cents={stats.gmv7} />
          </StatCell>
          <StatCell label="7 天新注册">{stats.newUsers7}</StatCell>
        </div>
      </section>
    </div>
  );
}
